/**
 * School-years API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type {
  SchoolYear,
  CreateSchoolYearDto,
  UpdateSchoolYearDto,
} from '@/types/school-year.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Append query params to a path when the search string is non-empty. */
function listUrl(path: string, searchParams: URLSearchParams): string {
  const q = searchParams.toString();
  return q ? `${path}?${q}` : path;
}

/** Trigger a browser download of an XLSX blob. */
function downloadXlsx(data: Blob, filename: string): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /academic/school-years` — paginated school years. */
export const getSchoolYears = async (params?: {
  page?: number;
  pageSize?: number;
  statuses?: string[];
  search?: string;
  isCurrent?: boolean;
  periodPhases?: ('ended' | 'upcoming')[];
}): Promise<PaginatedResponse<SchoolYear>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  if (params?.search) searchParams.append('search', params.search);
  if (params?.isCurrent !== undefined)
    searchParams.append('isCurrent', params.isCurrent.toString());
  params?.periodPhases?.forEach((p) => searchParams.append('periodPhases', p));
  const { data } = await apiClient.get<PaginatedResponse<SchoolYear>>(
    listUrl('/academic/school-years', searchParams),
  );
  return data;
};

/** `GET /academic/school-years/:id` */
export const getSchoolYear = async (id: string): Promise<SchoolYear> => {
  const { data } = await apiClient.get(`/academic/school-years/${id}`);
  return data;
};

/** `POST /academic/school-years` */
export const createSchoolYear = async (dto: CreateSchoolYearDto): Promise<SchoolYear> => {
  const { data } = await apiClient.post('/academic/school-years', dto);
  return data;
};

/** `PATCH /academic/school-years/:id` */
export const updateSchoolYear = async (
  id: string,
  dto: UpdateSchoolYearDto,
): Promise<SchoolYear> => {
  const { data } = await apiClient.patch(`/academic/school-years/${id}`, dto);
  return data;
};

/** `DELETE /academic/school-years/:id` */
export const deleteSchoolYear = async (id: string): Promise<void> => {
  await apiClient.delete(`/academic/school-years/${id}`);
};

/** `GET /academic/school-years/export` — download XLSX. */
export const exportSchoolYearsExcel = async (): Promise<void> => {
  const response = await apiClient.get('/academic/school-years/export', { responseType: 'blob' });
  downloadXlsx(response.data, `school-years-${new Date().toISOString().slice(0, 10)}.xlsx`);
};
